import React, { createContext, useContext, useReducer, useEffect, useState } from 'react';
import { AuthState } from '../types';

const DEBUG_LOGGING = process.env.REACT_APP_DEBUG_LOGGING === 'true';

// Debug logging utility
const debugLog = (message: string) => {
  if (DEBUG_LOGGING) {
    console.log(`DEBUG: ${message}`);
  }
};

interface AuthContextType {
  state: AuthState;
  loading: boolean;
  login: (token: string, user: any) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthAction =
  | { type: 'LOGIN_SUCCESS'; payload: { token: string; user: any } }
  | { type: 'LOGOUT' }
  | { type: 'INIT_AUTH'; payload: AuthState };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
      return {
        isAuthenticated: true,
        token: action.payload.token,
        user: action.payload.user,
      };
    case 'LOGOUT':
      return {
        isAuthenticated: false,
      };
    case 'INIT_AUTH':
      return action.payload;
    default:
      return state;
  }
};

const initialState: AuthState = {
  isAuthenticated: false,
};

// Validate token format and expiration
const validateToken = (token: string): boolean => {
  if (!token) return false;
  
  try {
    // Basic JWT format check (3 parts separated by dots)
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    
    // Decode payload to check expiration
    const payload = JSON.parse(atob(parts[1]));
    const now = Math.floor(Date.now() / 1000);
    
    // Check if token is expired (with 5-minute buffer)
    return payload.exp && payload.exp > (now + 300);
  } catch (error) {
    debugLog(`Token validation failed: ${error}`);
    return false;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const user = localStorage.getItem('authUser');
    debugLog(`AuthContext init - token: ${token ? 'present' : 'missing'}, user: ${user ? 'present' : 'missing'}`);
    
    if (token && user) {
      // Validate token before restoring auth state
      if (validateToken(token)) {
        dispatch({
          type: 'INIT_AUTH',
          payload: {
            isAuthenticated: true,
            token,
            user: JSON.parse(user),
          },
        });
        debugLog('Token validated, auth state restored');
      } else {
        // Clear invalid token
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
        debugLog('Invalid token found, cleared localStorage');
      }
    }
    setLoading(false);
  }, []);

  const login = (token: string, user: any) => {
    debugLog(`AuthContext login called with token: ${token ? 'present' : 'missing'}`);
    
    // Validate token before storing
    if (validateToken(token)) {
      localStorage.setItem('authToken', token);
      localStorage.setItem('authUser', JSON.stringify(user));
      debugLog('Valid token stored in localStorage');
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { token, user },
      });
    } else {
      debugLog('Invalid token rejected during login');
      throw new Error('Invalid authentication token');
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    dispatch({ type: 'LOGOUT' });
    debugLog('User logged out, localStorage cleared');
  };

  return (
    <AuthContext.Provider value={{ state, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
